import { DefaultValueConverters } from '@databases/collab/default-value-converters';
import { YDatabase } from '@databases/collab/y-database';
import { getCloudId, getVersionId } from '@databases/database-state';
import {
  currentHostFrameContext,
  getFrameContextReferenceId,
} from '@databases/database-state/state/host-frame-context';
import { logError } from '@databases/logs';
import { UserType } from '@databases/model/value';
import { Content } from '@databases/types/confluence';
import { Database } from '@databases/types/database';
import { IntlShape } from 'react-intl-next';
import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import * as Y from 'yjs';

import { getBasicCollabDatabaseFromContent } from '../../database/service';
import { generateId } from '../../utils';
import { selectCurrentUserId } from '../context/selectors';
import { applyDatabaseDiff } from '../databases/actions';
import { CommonRootState } from '../redux';
import { selectUserByIdOptional } from '../users/selectors';

import { CollabManager } from './collab-manager';
import {
  YJSDatabaseSubset,
  convertToDatabase,
  convertToDatabaseFromDiff,
} from './convert-to-database';
import collabSlice from './slice';

type Dispatcher<Result> = ThunkAction<Result, CommonRootState, void, AnyAction>;

export const buildDefaultValueConverters = (
  getState: () => CommonRootState
): DefaultValueConverters => {
  return {
    getCurrentUser: () => {
      const state = getState();
      const currentUserId = selectCurrentUserId(state);

      return {
        ...(selectUserByIdOptional(state, { accountId: currentUserId }) ?? {
          accountId: currentUserId,
        }),
        id: generateId(),
        type: UserType.Referenced,
      };
    },
  };
};

const onDocumentUpdate = (
  collabManager: CollabManager,
  dispatch: ThunkDispatch<CommonRootState, void, AnyAction>,
  options: {
    contentId: string;
    isReadOnly?: boolean;
  }
) => {
  const { contentId, isReadOnly } = options;
  collabManager.database.doc.on(
    'update',
    function onCollabManagerDocumentUpdate(update, origin, doc, transaction) {
      try {
        const collabDb = collabManager.database;

        const diff = collabDb.getDiff(transaction);

        dispatch(
          applyDatabaseDiff({
            contentId,
            diff,
            collabDatabase: collabDb,
            isReadOnly: isReadOnly ?? collabManager.isReadOnly,
          })
        );
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (e: unknown) {
        if (e instanceof Error) {
          // Capture exception and allow the user action to proceed
          logError(`Error applying database diff: ${e.message}`, e);
        }
      }
    }
  );
};

export const addCollabManager =
  (
    contentId: string,
    spaceId: number,
    referenceId: string,
    intl: IntlShape,
    initialDatabase?: YDatabase
  ): Dispatcher<Promise<CollabManager>> =>
  async (dispatch, getState) => {
    const cloudId = getCloudId();
    const versionId = getVersionId();

    if (!cloudId) {
      throw new Error('cloudId is not defined');
    }

    const emptyDb =
      initialDatabase || new YDatabase(undefined, buildDefaultValueConverters(getState));

    const token =
      getFrameContextReferenceId() === referenceId
        ? currentHostFrameContext.data
        : undefined;

    const collabManager = new CollabManager(
      cloudId,
      spaceId.toString(),
      contentId,
      referenceId,
      emptyDb,
      intl,
      token,
      versionId
    );

    onDocumentUpdate(collabManager, dispatch, {
      contentId,
      isReadOnly: false,
    });

    collabManager.onTelepointerChange((clientId, telepointerState) => {
      dispatch(
        collabSlice.actions.updateTelepointer({
          clientId,
          telepointerState,
        })
      );
    });

    dispatch(
      collabSlice.actions.addDatabase({
        contentId,
        collabManager,
      })
    );

    return collabManager;
  };

export const buildDatabaseFromCollabAndContent = (
  databaseContent: Content<'database'>,
  collabDatabase: YDatabase,
  isReadOnly: boolean,
  transaction?: Y.Transaction,
  baseDatabase?: YJSDatabaseSubset
) => {
  if (!databaseContent.referenceId) {
    throw new Error('missing referenceId');
  }

  const base = getBasicCollabDatabaseFromContent(databaseContent, isReadOnly);

  const collabDatabaseAsDatabase =
    transaction && baseDatabase
      ? convertToDatabaseFromDiff(
          collabDatabase.getDiff(transaction),
          collabDatabase,
          baseDatabase
        )
      : convertToDatabase(collabDatabase);

  const database: Database = {
    ...base,
    id: databaseContent.referenceId,
    referenceId: databaseContent.referenceId,

    metaVersion: 0,

    entriesContentId: '', // TODO: null this type
    entriesContentVersion: 0,

    ...collabDatabaseAsDatabase,
  };

  return database;
};

export const getOrCreateCollabManager =
  (
    {
      contentId,
      spaceId,
      referenceId,
    }: { contentId: string; spaceId: number; referenceId: string },
    intl: IntlShape,
    initialDatabase?: YDatabase
  ): Dispatcher<Promise<CollabManager>> =>
  async (dispatch, getState) => {
    const collabManager = getState().collab.byContentId[contentId];
    if (!collabManager && spaceId && referenceId) {
      const manager = await dispatch(
        addCollabManager(contentId, spaceId, referenceId, intl, initialDatabase)
      );

      manager.onPermissionsChanged((isReadOnly) => {
        dispatch(
          applyDatabaseDiff({
            contentId,
            diff: {
              fieldsOrderingChanged: false,
              entriesOrderingChanged: false,
              changedFieldIds: [],
              changedEntryIds: [],
              threadsChanged: false,
              viewsChanged: false,
              defaultEntryChanged: false,
              othersChanged: false,
            },
            collabDatabase: manager.database,
            isReadOnly,
          })
        );
      });

      onDocumentUpdate(manager, dispatch, {
        contentId,
      });

      return manager;
    } else {
      return collabManager;
    }
  };
